import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { cn } from '@achra/ui/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@achra/ui/table';
import { ArrowRight, ArrowUpDown, Copy } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { ProccesedWallets } from '@/modules/networks/hooks/use-wallets-card';
import { useWalletsTable } from '@/modules/networks/hooks/use-wallets-table';
import { SortEnum } from '@/modules/networks/types';
import { CopyButton, CopyTooltip, CopyTrigger } from '@/modules/shared/components/copy-button';
export interface WalletsTableProps {
  wallets: ProccesedWallets[];
  className?: string;
}
function WalletsTable(t0) {
  const $ = _c(25);
  const {
    wallets,
    className
  } = t0;
  let t1;
  if ($[0] !== wallets) {
    t1 = {
      wallets
    };
    $[0] = wallets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    headersSort,
    sortedWallets,
    proccesedWalletsTableColumns,
    handleSortClick,
    handleRowClick
  } = useWalletsTable(t1);
  let t2;
  if ($[2] !== className) {
    t2 = cn("[&_table]:block [&_table]:w-full", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== handleSortClick || $[5] !== headersSort || $[6] !== proccesedWalletsTableColumns) {
    let t4;
    if ($[8] !== handleSortClick || $[9] !== headersSort) {
      t4 = (column, index) => <TableHead key={column.accessorKey} className={cn("inline-block h-fit p-0!", column.isNumeric && "text-right", index === 0 && "w-[24%]", index === 1 && "w-[16%]", index === 2 && "w-[24%]", index === 3 && "w-[24%]")}><Button variant="ghost" aria-label={`Sort ${column.header} column`} onClick={() => {
          handleSortClick(index);
        }} className={cn("[&_path]:stroke-foreground/30 hover:[&_path]:stroke-foreground/50 active:[&_path]:stroke-foreground! h-fit p-0! font-semibold hover:bg-transparent lg:text-base/6 dark:hover:bg-transparent", headersSort[index] === SortEnum.Asc && "[&_path:nth-child(3)]:stroke-foreground [&_path:nth-child(4)]:stroke-foreground hover:[&_path:nth-child(3)]:stroke-foreground/50 hover:[&_path:nth-child(4)]:stroke-foreground/50 hover:[&_path:nth-child(1)]:stroke-foreground/30 hover:[&_path:nth-child(2)]:stroke-foreground/30", headersSort[index] === SortEnum.Desc && "[&_path:nth-child(1)]:stroke-foreground [&_path:nth-child(2)]:stroke-foreground hover:[&_path:nth-child(1)]:stroke-foreground/50 hover:[&_path:nth-child(2)]:stroke-foreground/50 hover:[&_path:nth-child(3)]:stroke-foreground/30 hover:[&_path:nth-child(4)]:stroke-foreground/30")}>{column.header}<ArrowUpDown className="size-4" /></Button></TableHead>;
      $[8] = handleSortClick;
      $[9] = headersSort;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = proccesedWalletsTableColumns.map(t4);
    $[4] = handleSortClick;
    $[5] = headersSort;
    $[6] = proccesedWalletsTableColumns;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableHead className="size-9 h-fit p-0! text-right" />;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3) {
    t5 = <TableHeader className="mb-2 inline-block w-full"><TableRow className="flex h-fit w-full justify-between border-b-0! px-2 py-4 xl:p-4 xl:pl-3 2xl:p-4">{t3}{t4}</TableRow></TableHeader>;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleRowClick || $[15] !== sortedWallets) {
    let t7;
    if ($[17] !== handleRowClick) {
      t7 = wallet => <TableRow key={wallet.id} className="flex h-fit w-full cursor-pointer items-center justify-between border-b-0! px-2 py-3 xl:p-3 xl:pr-4 2xl:py-3 2xl:pr-4 2xl:pl-3.5" onClick={event => {
        handleRowClick(event, wallet.address);
      }} aria-label={`View ${wallet.name} wallet`}><TableCell className="inline-block h-fit w-[24%] p-0!"><span className="text-wrap">{wallet.name}</span></TableCell><TableCell className="inline-block h-fit w-[16%] p-0!"><div className="flex items-center gap-2"><div className="flex items-center gap-1"><div className="relative size-6 overflow-hidden rounded-full"><Image src={wallet.image} alt={wallet.name} fill={true} priority={true} className="absolute" /></div><Link href="/network/powerhouse#wallets" target="_blank" className="text-status-progress font-normal">{wallet.shortAddress}</Link></div><CopyButton value={wallet.address}><CopyTooltip side="bottom" tooltip="Copy Address" copiedTooltip="Copied!"><CopyTrigger variant="icon" size="iconXsm" aria-label="Copy address"><Copy /></CopyTrigger></CopyTooltip></CopyButton></div></TableCell><TableCell className="inline-block h-fit w-[24%] p-0! text-right">{wallet.usdsBalance.toLocaleString()}</TableCell><TableCell className="inline-block h-fit w-[24%] p-0! text-right">{wallet.skyBalance.toLocaleString()}</TableCell><TableCell className="inline-block size-9 h-fit p-0! text-right"><Button variant="outline" size="icon" asChild={true}><Link href="/network/powerhouse#wallets" target="_blank" aria-label="View wallet"><ArrowRight className="size-4" /></Link></Button></TableCell></TableRow>;
      $[17] = handleRowClick;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t6 = sortedWallets.map(t7);
    $[14] = handleRowClick;
    $[15] = sortedWallets;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[19] !== t6) {
    t7 = <TableBody className="flex flex-col gap-2">{t6}</TableBody>;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t2 || $[22] !== t5 || $[23] !== t7) {
    t8 = <Table variant="pills" className={t2}>{t5}{t7}</Table>;
    $[21] = t2;
    $[22] = t5;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
export { WalletsTable };